import type { CommentThreadContextType } from './CommentThreadContext.ts';
import type { CommentShape, CommentThreadNode, CommentThreadNodeMap } from './types.ts';
import { createElement, useCallback, useEffect, useMemo, useState } from 'react';
import { CommentThreadContext } from './CommentThreadContext.ts';
import { buildNodeMap, buildNodes } from './utils.ts';

export interface CommentThreadProviderProps<TComment> {
  canReply?: boolean;
  children: React.ReactNode;
  commentComponent: CommentThreadContextType<TComment>['commentComponent'];
  emptyMessage?: CommentThreadContextType<TComment>['emptyMessage'];
  fetchQueryArgs: CommentThreadContextType<TComment>['fetchQueryArgs'];
  pinnedCommentId?: string;
  pinnedComments?: TComment[];
}

export function CommentThreadProvider<TComment extends CommentShape>({
  canReply = true,
  children,
  commentComponent,
  emptyMessage,
  fetchQueryArgs,
  pinnedCommentId,
  pinnedComments
}: CommentThreadProviderProps<TComment>) {
  const [rootNode, setRootNode] = useState<CommentThreadNode<TComment>>(() => ({
    id: null,
    children: [],
    isPinned: false,
    parentId: null
  }));

  const [nodeMap, setNodeMap] = useState<CommentThreadNodeMap<TComment>>(
    () => new Map([[null, rootNode]])
  );

  const addComments = useCallback<CommentThreadContextType<TComment>['addComments']>(
    (comments, options) => {
      setRootNode(root => {
        const newRootNode = buildNodes(root, comments, {
          ...options,
          pinnedId: pinnedCommentId
        });

        setNodeMap(buildNodeMap(newRootNode));

        return newRootNode;
      });
    },
    [pinnedCommentId]
  );

  useEffect(() => {
    if (pinnedComments?.length) {
      addComments(pinnedComments);
    }
  }, [addComments, pinnedComments]);

  return createElement(
    CommentThreadContext.Provider as React.Context<CommentThreadContextType<TComment>>['Provider'],
    {
      value: useMemo(
        () => ({
          addComments,
          canReply,
          commentComponent,
          emptyMessage,
          fetchQueryArgs,
          pinnedCommentId,
          nodeMap,
          rootNode
        }),
        [
          addComments,
          canReply,
          commentComponent,
          emptyMessage,
          fetchQueryArgs,
          pinnedCommentId,
          nodeMap,
          rootNode
        ]
      )
    },
    children
  );
}
